import React from 'react';

// Material UI Components
import SvgIcon from '@mui/material/SvgIcon';
import { useTheme } from '@mui/material/styles';

const R = 1;
const SQUARE_OUTLINE =
  'M18 4H6c-1 0-2 1-2 2v12c0 1.1.9 2 2 2h12c1 0 2-1 2-2V6c0-1-1-2-2-2';
const SQUARE_FILL_PATH = `${SQUARE_OUTLINE}z`;

// r=1 rounded rect; ring cutout is a second subpath after zm (Material checkbox technique).
const roundedRect = (left, top, right, bottom) =>
  `M${left + R} ${top}H${right - R}Q${right} ${top} ${right} ${top + R}V${bottom - R}Q${right} ${bottom} ${right - R} ${bottom}H${left + R}Q${left} ${bottom} ${left} ${bottom - R}V${top + R}Q${left} ${top} ${left + R} ${top}z`;

const ringCutout = (left, top, right, bottom) =>
  `zm0 ${bottom - 4}H${left + R}Q${left} ${bottom} ${left} ${bottom - R}V${top + R}Q${left} ${top} ${left + R} ${top}H${right - R}Q${right} ${top} ${right} ${top + R}V${bottom - R}Q${right} ${bottom} ${right - R} ${bottom}z`;

const squareRing = (left, top, right, bottom) => `${SQUARE_OUTLINE}${ringCutout(left, top, right, bottom)}`;

const EMPTY_SQUARE_SHARP_PATH = `${SQUARE_OUTLINE}zm0 14H6V6h12v12z`;
const EMPTY_SQUARE_ROUNDED_PATH = squareRing(6, 6, 18, 18);
const FILLED_INNER_PATH = roundedRect(5, 5, 19, 19);
const FILLED_RING_PATH = squareRing(5, 5, 19, 19);

export const FilledSquareIcon = ({ bordered, innerFillOpacity, ringColor, ...props }) => {
  const theme = useTheme();
  const ringFill = ringColor ?? theme.palette.action.disabled;

  return (
    <SvgIcon {...props} data-testid="FilledSquareIcon">
      {bordered ? (
        <>
          <path fill="currentColor" fillOpacity={innerFillOpacity ?? 1} d={FILLED_INNER_PATH} />
          <path fill={ringFill} d={FILLED_RING_PATH} />
        </>
      ) : (
        <path d={SQUARE_FILL_PATH} />
      )}
    </SvgIcon>
  );
};

export const EmptySquareIcon = ({ roundedInner, ...props }) => (
  <SvgIcon {...props} data-testid="EmptySquareIcon">
    <path d={roundedInner ? EMPTY_SQUARE_ROUNDED_PATH : EMPTY_SQUARE_SHARP_PATH} />
  </SvgIcon>
);

export const TasksIcon = (props) => (
  <SvgIcon {...props} data-testid="TasksIcon">
    <path
      fillRule="evenodd"
      d="M 20,2 H 8 C 6.9,2 6,2.9 6,4 v 12 c 0,1.1 0.9,2 2,2 h 12 c 1.1,0 2,-0.9 2,-2 V 4 C 22,2.9 21.1,2 20,2 z
    m 0.13,4.85 -1.4,-1.4 -6.6,6.6 -2.6,-2.6 -1.4,1.4 4,4 Z M 2,6 v 14 c 0,1.1 0.9,2 2,2 H 18 V 20 H 4 V 6 Z"
    />
  </SvgIcon>
);

export const TaskCircleIcon = (props) => (
  <SvgIcon {...props} data-testid="TaskCircleIcon">
    <path fillRule="evenodd" d=" M 12,2 C 6.48,2 2,6.48 2,12 2,17.52 6.48,22 12,22 17.52,22 22,17.52 22,12 22,6.48 17.52,2 12,2 Z m 6,7 -1.4,-1.4 -6.6,6.6 -2.6,-2.6 -1.4,1.4 4,4 Z" />
  </SvgIcon>
);

export const TaskReportIcon = (props) => (
  <SvgIcon {...props} data-testid="TaskReportIcon">
    <path
      fillRule="evenodd"
      d="M19 3h-4.18C14.4 1.84 13.3 1 12 1c-1.3 0-2.4.84-2.82 2H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2z
m-7 0c.55 0 1 .45 1 1s-.45 1-1 1-1-.45-1-1 .45-1 1-1zm 6,7 -1.4,-1.4 -6.6,6.6 -2.6,-2.6 -1.4,1.4 4,4 Z"
    />
  </SvgIcon>
);

export const AssessmentEditOffIcon = React.forwardRef((props, ref) => (
  <SvgIcon ref={ref} {...props} data-testid="AssessmentEditOffIcon">
    <path
      fillRule="evenodd"
      d="M19 3h-4.18C14.4 1.84 13.3 1 12 1c-1.3 0-2.4.84-2.82 2H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zm-7 0c.55 0 1 .45 1 1s-.45 1-1 1-1-.45-1-1 .45-1 1-1z
M12.382 9.881l1.259-1.259 2.436 2.436-1.259 1.26z
M17.962 8.259l-1.521-1.521a.65.65 0 0 0-.916 0l-1.19 1.19 2.438 2.438L17.962 9.15a.65.65 0 0 0 0-.891z
M5.8 7.85l4.309 4.309L6.45 15.813V18.25h2.438l3.659-3.653L16.2 18.25l1.3-1.3L7.1 6.55z"
    />
  </SvgIcon>
));

export const CommunityIcon = (props) => (
  <SvgIcon {...props} data-testid="CommunityIcon">
    <path d="m 0.69,10.29 h 2.24 v 6.05 h 3.78 v 3.36 h 10.56 v -3.36 h 3.78 v -6.05 h 2.24 l -7.56,-6.80 -3.78,3.36 5.32,4.73 v 1.48 l -5.32,-4.76 -5.24,4.76 v -1.48 l 5.24,-4.73 -3.81,-3.36 z" />
  </SvgIcon>
);

export const DivisionIcon = (props) => (
  <SvgIcon {...props} data-testid="DivisionIcon">
    <path d="m 7.75,3.7 -3.5,3.15 v 4.75 h 7 V 6.85 Z m 8.5,0 -3.5,3.15 v 4.75 h 7 V 6.85 Z m -8.5,8.5 -3.5,3.15 v 4.75 h 7 v -4.75 z m 8.5,0 -3.5,3.15 v 4.75 h 7 v -4.75 z" />
  </SvgIcon>
);

export const LotCircleIcon = (props) => (
  <SvgIcon {...props} data-testid="LotCircleIcon">
    <path fillRule="evenodd" d="m 17,17.5 v -5.75 h 2.15 L 12,5.25 4.75,11.75 H 6.9 V 17.5 Z M 12,2 C 6.48,2 2,6.48 2,12 2,17.52 6.48,22 12,22 17.52,22 22,17.52 22,12 22,6.48 17.52,2 12,2 Z" />
  </SvgIcon>
);

export const CommunityCircleIcon = (props) => (
  <SvgIcon {...props} data-testid="CommunityCircleIcon">
    <path
      fillRule="evenodd"
      d="m 5.28,11.23 h 1.33 v 3.6 h 2.25 v 2 h 6.25 v -2 h 2.25 v -3.6 h 1.33 L 14.25,7.18 11.98,9.18 15.15,12 v 0.9 L 11.98,10.05 8.85,12.9 V 12 L 11.98,9.18 9.71,7.18 Z M 12,2 C 6.48,2 2,6.48 2,12 2,17.52 6.48,22 12,22 17.52,22 22,17.52 22,12 22,6.48 17.52,2 12,2 Z"
    />
  </SvgIcon>
);

export const DivisionCircleIcon = (props) => (
  <SvgIcon {...props} data-testid="DivisionCircleIcon">
    <path
      fillRule="evenodd"
      d="M 12,2 C 6.48,2 2,6.48 2,12 2,17.52 6.48,22 12,22 17.52,22 22,17.52 22,12 22,6.48 17.52,2 12,2 Z m 3.61,9.66 -2.98,2.68 V 18.37 h 5.95 v -4.04 z m -7.23,0 -2.98,2.68 V 18.37 h 5.95 v -4.04 z m 7.23,-7.23 -2.98,2.68 v 4.04 h 5.95 V 7.11 Z m -7.23,0 -2.98,2.68 v 4.04 h 5.95 V 7.11 Z"
    />
  </SvgIcon>
);

export const LotReportIcon = (props) => (
  <SvgIcon {...props} data-testid="LotReportIcon">
    <path
      fillRule="evenodd"
      d="m 15.99,18.31 v -4.6 h 1.72 L 11.99,8.51 6.19,13.71 h 1.72 v 4.6 z M 12,4 c 0.55,0 1,0.45 1,1 0,0.55 -0.45,1 -1,1 -0.55,0 -1,-0.45 -1,-1 0,-0.55 0.45,-1 1,-1 z m 7,0 H 14.82 C 14.4,2.84 13.3,2 12,2 10.7,2 9.6,2.84 9.18,4 H 5 C 3.9,4 3,4.9 3,6 v 14 c 0,1.1 0.9,2 2,2 h 14 c 1.1,0 2,-0.9 2,-2 V 6 C 21,4.9 20.1,4 19,4 Z"
    />
  </SvgIcon>
);

export const CommunityReportIcon = (props) => (
  <SvgIcon {...props} data-testid="CommunityReportIcon">
    <path
      fillRule="evenodd"
      d="m 5.28,12.75 h 1.33 v 3.6 h 2.25 v 2 h 6.25 v -2 h 2.25 v -3.6 h 1.33 L 14.25,8.70 11.98,10.70 15.15,13.53 v 0.9 l -3.18,-2.85 -3.13,2.85 v -0.9 L 11.98,10.70 9.71,8.70 Z M 12,4 c 0.55,0 1,0.45 1,1 0,0.55 -0.45,1 -1,1 -0.55,0 -1,-0.45 -1,-1 0,-0.55 0.45,-1 1,-1 z m 7,0 H 14.82 C 14.4,2.84 13.3,2 12,2 10.7,2 9.6,2.84 9.18,4 H 5 C 3.9,4 3,4.9 3,6 v 14 c 0,1.1 0.9,2 2,2 h 14 c 1.1,0 2,-0.9 2,-2 V 6 C 21,4.9 20.1,4 19,4 Z"
    />
  </SvgIcon>
);

export const DivisionReportIcon = (props) => (
  <SvgIcon {...props} data-testid="DivisionReportIcon">
    <path
      fillRule="evenodd"
      d="m 9.11,7.32 -2.38,2.14 v 3.23 h 4.76 V 9.46 Z m 5.78,0 -2.38,2.14 v 3.23 h 4.76 V 9.46 Z m -5.78,5.78 -2.38,2.14 v 3.23 h 4.76 v -3.23 z m 5.78,0 -2.38,2.14 v 3.23 h 4.76 V 15.24 Z M 12,4 c 0.55,0 1,0.45 1,1 0,0.55 -0.45,1 -1,1 -0.55,0 -1,-0.45 -1,-1 0,-0.55 0.45,-1 1,-1 z m 7,0 H 14.82 C 14.4,2.84 13.3,2 12,2 10.7,2 9.6,2.84 9.18,4 H 5 C 3.9,4 3,4.9 3,6 v 14 c 0,1.1 0.9,2 2,2 h 14 c 1.1,0 2,-0.9 2,-2 V 6 C 21,4.9 20.1,4 19,4 Z"
    />
  </SvgIcon>
);

// Circle: "M 12,2 C 6.48,2 2,6.48 2,12 2,17.52 6.48,22 12,22 17.52,22 22,17.52 22,12 22,6.48 17.52,2 12,2 Z"

export const SystemSafeIcon = (props) => (
  <SvgIcon {...props} data-testid="SystemSafeIcon">
    <path
      fillRule="evenodd"
      d="m 11.86,2.44 c -0.95,0 -1.74,0.16 -2.41,0.40 v 5.68 c 0,0.46 -0.37,0.83 -0.82,0.83 -0.45,0 -0.82,-0.37 -0.82,-0.83 V 3.70 C 7.33,4.04 6.98,4.37 6.74,4.54 2.45,7.71 1.63,12.72 1.62,17.86 c 0,0.83 -0.72,1.43 -0.72,1.43 -0.68,0.52 -0.21,1.62 0.63,1.48 0,0 6.99,-1.14 10.34,-1.14 3.35,0 10.43,1.14 10.43,1.14 0.69,0.11 1.20,-0.65 0.83,-1.26 -0.19,-0.66 -0.99,-0.83 -1.01,-1.65 0,-4.25 -0.50,-9.69 -5.12,-13.32 C 16.74,4.35 16.42,4.04 16,3.72 v 4.80 c 0,0.46 -0.37,0.83 -0.82,0.83 -0.45,0 -0.82,-0.37 -0.82,-0.83 V 2.83 C 13.69,2.60 12.88,2.44 11.86,2.44 Z m 0,13.48 c 0.95,0.00 2.17,0.05 3.47,0.30 0.44,0.09 0.73,0.52 0.65,0.97 -0.09,0.45 -0.52,0.74 -0.96,0.66 -1.13,-0.22 -2.25,-0.27 -3.16,-0.27 -0.88,-0.00 -2.07,0.11 -3.10,0.27 -0.45,0.07 -0.87,-0.23 -0.94,-0.69 -0.07,-0.45 0.23,-0.88 0.68,-0.95 1.10,-0.18 2.34,-0.30 3.36,-0.30 z"
    />
  </SvgIcon>
);

export const SystemSiteIcon = (props) => (
  <SvgIcon {...props} data-testid="SystemSiteIcon">
    <path fillRule="evenodd" d="M 15.22,0.00 8.68,14.48 h 5.06 v 4.14 C 8.69,16.10 4.00,13.14 0,13.14 v 10.86 h 24 v -3.14 c -2.65,0.12 -4.94,0.06 -7.19,-0.93 v -5.45 h 4.96 z" />
  </SvgIcon>
);

export const SystemFounIcon = (props) => (
  <SvgIcon {...props} data-testid="SystemFounIcon">
    <path
      fillRule="evenodd"
      d="M 0.22,-2.75e-5 -1.81e-5,0.22 V 2.44 L 2.44,-2.75e-5 Z m 6.57,0 L -1.81e-5,6.80 V 9.01 L 8.88,0.13 V 4.25 L -1.81e-5,13.14 v 2.21 L 8.88,6.47 v 4.13 L 7.07,12.40 H 4.06 v 3.02 L -1.81e-5,19.48 v 2.46 L 4.06,17.88 H 7.93 l -5.93,5.93 h 2.48 l 5.93,-5.93 h 3.85 l -5.93,5.93 h 2.21 l 5.93,-5.93 h 3.27 v -3.27 l 4.06,-4.06 V 8.34 L 19.75,12.40 H 15.90 L 23.81,4.49 V 2.00 L 14.93,10.89 V 7.01 L 21.94,0 H 19.47 L 14.93,4.55 V 0.42 L 15.35,0 Z M 23.81,14.68 l -9.13,9.13 h 2.21 l 6.92,-6.92 z m 0,6.57 -2.56,2.56 h 2.21 l 0.35,-0.35 z"
    />
  </SvgIcon>
);

export const SystemFrmgIcon = (props) => (
  <SvgIcon {...props} data-testid="SystemFrmgIcon">
    <path
      fillRule="evenodd"
      d="M 17.85,0.05 3.66,8.35 5.30,9.29 5.31,10.60 6.43,9.95 6.36,21.84 4.69,20.92 3.66,21.51 l 1.64,0.94 0.01,1.30 14.27,-8.24 v -1.31 L 18.11,13.36 V 3.21 l 1.47,-0.85 0.00,-1.31 z m -0.93,3.84 0.01,11.85 -1.67,-0.92 -2.39,1.36 V 6.23 Z M 11.68,6.92 11.67,18.78 9.99,17.86 7.66,19.20 V 9.26 Z"
    />
  </SvgIcon>
);

export const SystemDrgpIcon = (props) => (
  <SvgIcon {...props} data-testid="SystemDrgpIcon">
    <path
      fillRule="evenodd"
      d="M 18.38,0 6.65,6.76 v 9.31 l 4.86,-2.81 c -0.01,-1.78 -0.00,-3.60 -0.00,-5.39 0,-0.61 0.28,-0.61 0.44,0 0.11,0.41 0.50,1.61 0.80,2.25 0.20,0.41 0.85,0.84 1.30,1.31 0.07,0.08 0.14,0.16 0.22,0.24 l 4.12,-2.38 z m -1.03,11.02 -2.53,1.46 c 0.10,0.23 0.16,0.48 0.16,0.75 0,0.54 -0.10,1.05 -0.40,1.45 -0.29,0.40 -0.66,0.64 -1.23,0.65 -0.39,0.01 -0.88,-0.04 -1.27,-0.40 -0.18,-0.16 -0.31,-0.38 -0.41,-0.64 l -6.05,3.49 v 6.23 L 17.35,17.23 Z"
    />
  </SvgIcon>
);

export const SystemThrmIcon = (props) => (
  <SvgIcon {...props} data-testid="SystemThrmIcon">
    <path
      fillRule="evenodd"
      d="M 12.23,0.09 V 23.91 h 9.71 V 0.09 Z m -10.11,4.32e-6 C 1.83,0.59 1.69,1.20 1.69,1.91 c 0,0.81 0.18,1.49 0.55,2.01 0.15,0.21 0.35,0.37 0.55,0.52 -0.20,0.14 -0.40,0.30 -0.55,0.52 -0.37,0.52 -0.55,1.20 -0.55,2.01 0,0.81 0.18,1.49 0.55,2.01 0.15,0.21 0.35,0.37 0.55,0.52 -0.20,0.14 -0.40,0.30 -0.55,0.52 -0.37,0.52 -0.55,1.20 -0.55,2.01 0,0.81 0.18,1.49 0.55,2.01 0.15,0.21 0.35,0.37 0.55,0.52 -0.20,0.14 -0.40,0.30 -0.55,0.52 -0.37,0.52 -0.55,1.20 -0.55,2.01 0,0.81 0.18,1.49 0.55,2.01 0.15,0.21 0.35,0.37 0.55,0.52 -0.20,0.14 -0.40,0.30 -0.55,0.52 -0.37,0.52 -0.55,1.20 -0.55,2.01 0,0.70 0.14,1.31 0.42,1.80 h 4.78 c 0.01,-0.01 0.02,-0.02 0.03,-0.03 0.46,-0.46 0.93,-0.65 1.30,-0.67 0.12,-0.01 0.23,0.01 0.33,0.03 0.26,0.08 0.49,0.25 0.61,0.66 h 1.54 c -0.15,-0.78 -0.53,-1.42 -1.07,-1.80 0.70,-0.50 1.14,-1.42 1.14,-2.52 0,-1.11 -0.45,-2.02 -1.14,-2.52 0.70,-0.50 1.14,-1.42 1.14,-2.52 0,-1.11 -0.45,-2.02 -1.14,-2.52 0.70,-0.50 1.14,-1.42 1.14,-2.52 0,-1.11 -0.45,-2.02 -1.14,-2.52 0.70,-0.50 1.14,-1.41 1.14,-2.52 0,-1.11 -0.45,-2.02 -1.14,-2.52 C 10.18,1.52 10.57,0.88 10.72,0.09 H 9.17 C 9.06,0.52 8.82,0.70 8.55,0.77 8.18,0.89 7.53,0.75 6.92,0.14 6.91,0.12 6.89,0.11 6.87,0.09 Z m 2.08,0.36 c 0.47,0.00 1.14,0.27 1.65,0.77 0.28,0.28 0.59,0.51 0.91,0.69 C 6.43,2.09 6.12,2.32 5.84,2.61 5.27,3.18 4.47,3.44 4.01,3.36 3.77,3.32 3.62,3.23 3.48,3.04 3.34,2.84 3.21,2.49 3.21,1.91 c 0,-0.58 0.13,-0.93 0.27,-1.12 0.14,-0.20 0.29,-0.28 0.52,-0.33 0.06,-0.01 0.12,-0.02 0.19,-0.01 z M 8.23,3.02 c 0.12,-0.01 0.23,0.01 0.33,0.03 0.38,0.11 0.70,0.41 0.70,1.39 0,0.98 -0.32,1.27 -0.70,1.38 C 8.18,5.93 7.53,5.80 6.92,5.19 6.61,4.88 6.26,4.64 5.89,4.44 6.26,4.23 6.61,3.99 6.92,3.68 7.38,3.23 7.86,3.04 8.23,3.02 Z M 4.19,5.50 c 0.47,0.00 1.14,0.27 1.65,0.77 0.28,0.28 0.59,0.51 0.91,0.69 C 6.43,7.14 6.12,7.37 5.84,7.65 5.27,8.23 4.47,8.49 4.01,8.41 3.77,8.37 3.62,8.28 3.48,8.08 3.34,7.89 3.21,7.54 3.21,6.96 c 0,-0.58 0.13,-0.93 0.27,-1.12 0.14,-0.20 0.29,-0.28 0.52,-0.32 0.06,-0.01 0.12,-0.02 0.19,-0.01 z m 4.03,2.57 c 0.12,-0.01 0.23,0.01 0.33,0.03 0.38,0.11 0.70,0.40 0.70,1.38 0,0.98 -0.32,1.27 -0.70,1.39 C 8.18,10.98 7.53,10.85 6.92,10.24 6.61,9.93 6.26,9.69 5.89,9.48 6.26,9.28 6.61,9.04 6.92,8.73 7.38,8.28 7.86,8.09 8.23,8.07 Z M 4.19,10.55 c 0.47,0.00 1.14,0.27 1.65,0.77 0.28,0.28 0.59,0.51 0.91,0.69 -0.32,0.18 -0.63,0.41 -0.91,0.69 -0.58,0.58 -1.37,0.84 -1.84,0.75 -0.23,-0.04 -0.38,-0.13 -0.52,-0.32 -0.14,-0.20 -0.27,-0.54 -0.27,-1.12 0,-0.58 0.13,-0.93 0.27,-1.12 0.14,-0.20 0.29,-0.28 0.52,-0.32 0.06,-0.01 0.12,-0.02 0.19,-0.01 z m 4.03,2.57 c 0.12,-0.01 0.23,0.01 0.33,0.03 0.38,0.11 0.70,0.40 0.70,1.38 0,0.98 -0.32,1.27 -0.70,1.39 C 8.18,16.03 7.53,15.89 6.92,15.28 6.61,14.98 6.26,14.73 5.89,14.53 6.26,14.33 6.61,14.09 6.92,13.78 7.38,13.32 7.86,13.13 8.23,13.11 Z m -4.03,2.48 c 0.47,0.00 1.14,0.27 1.65,0.77 0.28,0.28 0.59,0.51 0.91,0.69 -0.32,0.18 -0.63,0.41 -0.91,0.69 -0.58,0.58 -1.37,0.84 -1.84,0.75 -0.23,-0.04 -0.38,-0.13 -0.52,-0.32 -0.14,-0.20 -0.27,-0.54 -0.27,-1.12 0,-0.58 0.13,-0.93 0.27,-1.12 0.14,-0.20 0.29,-0.28 0.52,-0.32 0.06,-0.01 0.12,-0.02 0.19,-0.01 z m 4.03,2.57 c 0.12,-0.01 0.23,0.01 0.33,0.03 0.38,0.11 0.70,0.41 0.70,1.39 0,0.98 -0.32,1.27 -0.70,1.38 C 8.18,21.08 7.53,20.94 6.92,20.33 6.61,20.02 6.26,19.78 5.89,19.58 6.26,19.38 6.61,19.14 6.92,18.83 7.38,18.37 7.86,18.18 8.23,18.16 Z m -4.03,2.48 c 0.47,0.00 1.14,0.27 1.65,0.77 0.28,0.28 0.59,0.51 0.91,0.69 -0.32,0.18 -0.63,0.41 -0.91,0.69 C 5.27,23.37 4.47,23.63 4.01,23.55 3.77,23.51 3.62,23.42 3.48,23.23 3.34,23.03 3.21,22.68 3.21,22.10 c 0,-0.58 0.13,-0.93 0.27,-1.12 0.14,-0.20 0.29,-0.28 0.52,-0.32 0.06,-0.01 0.12,-0.02 0.19,-0.01 z"
    />
  </SvgIcon>
);

export const SystemAirbIcon = (props) => (
  <SvgIcon {...props} data-testid="SystemAirbIcon">
    <path
      fillRule="evenodd"
      d="M 6.24e-7,0 V 24 H 10.00 V 0 Z M 15.66,0.31 c -1.31,0 -2.50,0.49 -3.32,1.37 -0.83,0.88 -1.28,2.12 -1.28,3.54 0,3.36 2.48,5.44 5.00,5.44 h 7.94 V 9.08 h -7.94 c -1.69,0 -3.41,-1.25 -3.41,-3.85 0,-1.08 0.33,-1.90 0.85,-2.45 0.52,-0.55 1.24,-0.87 2.16,-0.87 2.11,0 3.08,1.31 3.03,2.28 -0.02,0.40 -0.22,0.76 -0.49,1.11 L 17.39,4.51 16.42,8.30 16.77,8.20 20.19,7.26 19.34,6.43 C 19.80,5.88 20.23,5.18 20.27,4.26 V 4.26 C 20.38,2.22 18.46,0.31 15.66,0.31 Z M 16.06,13.33 c -2.52,0 -5.00,2.09 -5.00,5.44 0,1.42 0.45,2.66 1.28,3.54 0.83,0.88 2.01,1.38 3.32,1.38 2.80,0 4.72,-1.91 4.61,-3.96 -0.05,-0.91 -0.47,-1.62 -0.93,-2.16 l 0.84,-0.83 -3.77,-1.04 0.09,0.36 0.88,3.43 0.80,-0.78 c 0.28,0.36 0.47,0.72 0.49,1.11 0.05,0.98 -0.92,2.28 -3.03,2.28 -0.93,0 -1.64,-0.32 -2.16,-0.87 -0.52,-0.55 -0.85,-1.37 -0.85,-2.45 0,-2.61 1.73,-3.85 3.41,-3.85 h 7.94 v -1.59 z"
    />
  </SvgIcon>
);

export const SystemPlmbIcon = (props) => (
  <SvgIcon {...props} data-testid="SystemPlmbIcon">
    <path
      fillRule="evenodd"
      d="m 16.36,0.40 c -0.08,0 -0.15,0 -0.24,0.02 L 5.67,0.41 c -1.07,0 -2.01,0.56 -2.65,1.39 C 2.36,2.62 2,3.72 1.98,4.91 v 0 L 1.97,24 H 10.82 V 9.45 h 4.70 v 4.97 c 0,1.58 0.34,2.65 1.05,3.31 0.70,0.65 1.59,0.73 2.29,0.71 1.03,-0.01 1.91,-0.45 2.45,-1.18 0.53,-0.71 0.73,-1.63 0.73,-2.62 0,-1.32 -0.69,-2.41 -1.47,-3.34 -0.78,-0.93 -1.69,-1.72 -2.34,-2.47 0.31,-0.21 0.58,-0.49 0.82,-0.78 0.64,-0.83 1.01,-1.93 1.01,-3.13 0,-1.19 -0.37,-2.28 -1.01,-3.11 C 18.39,0.97 17.43,0.40 16.36,0.40 Z m 0,1.55 c 0.53,6.2e-6 1.03,0.27 1.45,0.80 0.42,0.52 0.69,1.31 0.69,2.18 0,0.87 -0.29,1.65 -0.69,2.18 -0.04,0.05 -0.08,0.10 -0.12,0.14 C 17.64,7.09 17.58,6.95 17.52,6.81 17.40,6.51 17.27,6.21 16.96,5.95 16.38,5.44 15.68,5.37 15.16,5.37 c -0.49,9.25e-4 -0.87,0.08 -0.90,0.10 -0.02,-0.18 -0.04,-0.36 -0.04,-0.55 0,-0.87 0.29,-1.65 0.69,-2.18 0.20,-0.26 0.43,-0.45 0.67,-0.59 0.39,-0.15 0.70,-0.20 0.70,-0.20 v 0 c 0.02,0 0.06,0 0.08,0 z m -2.10,3.53 c 0.10,0.64 0.33,1.21 0.65,1.63 0.19,0.24 0.40,0.43 0.62,0.56 V 9.33 C 14.80,9.12 14.16,8.65 13.68,8.05 13.22,7.44 12.90,6.70 12.75,5.87 Z"
    />
  </SvgIcon>
);

export const SystemHvacIcon = (props) => (
  <SvgIcon {...props} data-testid="SystemHvacIcon">
    <path
      fillRule="evenodd"
      d="m 18.74,2.52 c -1.72,0 -3.00,0.74 -3.76,1.72 -0.75,0.97 -1.03,2.12 -1.03,3.07 A 0.82,0.82 0 0 0 14.78,8.13 0.82,0.82 0 0 0 15.60,7.31 c 0,-0.61 0.21,-1.44 0.69,-2.06 0.48,-0.62 1.18,-1.08 2.45,-1.08 1.28,0 2.02,0.46 2.53,1.08 0.51,0.62 0.73,1.45 0.73,2.06 0,0.68 -0.15,1.53 -0.59,2.14 -0.44,0.61 -1.14,1.09 -2.66,1.09 H 1.18 A 0.82,0.82 0 0 0 0.35,11.36 0.82,0.82 0 0 0 1.18,12.18 H 18.74 c 1.91,0 3.25,-0.75 3.99,-1.77 0.74,-1.02 0.91,-2.20 0.91,-3.10 0,-0.98 -0.32,-2.13 -1.10,-3.09 C 21.76,3.25 20.46,2.52 18.74,2.52 Z M 8.63,2.75 c -1.14,0 -2.01,0.53 -2.48,1.18 -0.47,0.65 -0.62,1.35 -0.74,1.79 A 0.82,0.82 0 0 0 5.99,6.73 0.82,0.82 0 0 0 6.99,6.15 C 7.12,5.68 7.26,5.20 7.48,4.90 7.70,4.59 7.92,4.39 8.63,4.39 c 0.71,0 1.04,0.23 1.28,0.53 0.24,0.30 0.35,0.73 0.35,1.02 0,0.21 -0.16,0.71 -0.45,1.07 C 9.50,7.37 9.13,7.61 8.63,7.61 H 1.18 a 0.82,0.82 0 0 0 -0.82,0.82 0.82,0.82 0 0 0 0.82,0.82 h 7.45 c 1.05,0 1.91,-0.54 2.44,-1.19 0.54,-0.65 0.83,-1.39 0.83,-2.12 0,-0.64 -0.19,-1.40 -0.71,-2.05 -0.52,-0.65 -1.42,-1.14 -2.56,-1.14 z M 1.18,13.52 A 0.82,0.82 0 0 0 0.35,14.34 0.82,0.82 0 0 0 1.18,15.17 H 11.41 c 0.52,0 1.03,0.18 1.39,0.52 0.36,0.34 0.63,0.84 0.63,1.69 0,1.67 -1.22,2.46 -2.02,2.46 -0.48,0 -1.20,-0.17 -1.71,-0.56 C 9.18,18.89 8.81,18.36 8.81,17.38 A 0.82,0.82 0 0 0 7.99,16.55 0.82,0.82 0 0 0 7.16,17.38 c 0,1.46 0.69,2.57 1.55,3.22 0.87,0.64 1.87,0.88 2.69,0.88 1.81,0 3.67,-1.62 3.67,-4.10 0,-1.23 -0.45,-2.24 -1.16,-2.90 -0.70,-0.66 -1.62,-0.96 -2.51,-0.96 z"
    />
  </SvgIcon>
);

export const SystemElecIcon = (props) => (
  <SvgIcon {...props} data-testid="SystemElecIcon">
    <path fillRule="evenodd" d="M 12,0.09 V 9.35 L 2.74,8.03 12,23.91 v -7.94 l 9.26,1.32 z" />
  </SvgIcon>
);

export const SystemShwrIcon = (props) => (
  <SvgIcon {...props} data-testid="SystemShwrIcon">
    <path
      fillRule="evenodd"
      d="m 13.58,18.47 a 0.75,0.75 0 0 0 -0.23,0.03 0.75,0.75 0 0 0 -0.50,0.94 l 0.83,2.71 a 0.75,0.75 0 0 0 0.94,0.50 0.75,0.75 0 0 0 0.50,-0.94 L 14.29,19.00 a 0.75,0.75 0 0 0 -0.71,-0.53 z m -1.24,-3.74 a 0.75,0.75 0 0 0 -0.23,0.03 0.75,0.75 0 0 0 -0.50,0.94 l 0.40,1.32 a 0.75,0.75 0 0 0 0.94,0.50 0.75,0.75 0 0 0 0.50,-0.94 l -0.40,-1.32 a 0.75,0.75 0 0 0 -0.71,-0.53 z m 4.27,0.04 a 0.75,0.75 0 0 0 -0.45,0.15 0.75,0.75 0 0 0 -0.16,1.05 l 2.11,2.86 a 0.75,0.75 0 0 0 1.05,0.16 0.75,0.75 0 0 0 0.16,-1.05 l -2.11,-2.86 a 0.75,0.75 0 0 0 -0.60,-0.30 z m -2.45,-2.87 a 0.75,0.75 0 0 0 -0.51,0.20 0.75,0.75 0 0 0 -0.05,1.06 l 0.91,1 a 0.75,0.75 0 0 0 1.06,0.05 0.75,0.75 0 0 0 0.05,-1.06 L 14.71,12.14 a 0.75,0.75 0 0 0 -0.55,-0.24 z m 4.39,-0.71 a 0.75,0.75 0 0 0 -0.59,0.29 0.75,0.75 0 0 0 0.13,1.05 l 2.79,2.16 a 0.75,0.75 0 0 0 1.05,-0.13 0.75,0.75 0 0 0 -0.13,-1.05 L 19.02,11.35 A 0.75,0.75 0 0 0 18.56,11.19 Z M 15.33,9.05 a 0.75,0.75 0 0 0 -0.64,0.35 0.75,0.75 0 0 0 0.24,1.03 l 1.15,0.72 A 0.75,0.75 0 0 0 17.12,10.91 0.75,0.75 0 0 0 16.88,9.88 L 15.72,9.16 A 0.75,0.75 0 0 0 15.33,9.05 Z m 4.28,-1.62 a 0.75,0.75 0 0 0 -0.69,0.47 0.75,0.75 0 0 0 0.42,0.97 l 3.00,1.19 a 0.75,0.75 0 0 0 0.97,-0.42 0.75,0.75 0 0 0 -0.42,-0.97 L 19.89,7.48 a 0.75,0.75 0 0 0 -0.28,-0.05 z M 16.01,6.23 a 0.75,0.75 0 0 0 -0.70,0.52 0.75,0.75 0 0 0 0.48,0.95 l 1.15,0.38 A 0.75,0.75 0 0 0 17.89,7.59 0.75,0.75 0 0 0 17.41,6.64 L 16.25,6.27 A 0.75,0.75 0 0 0 16.01,6.23 Z m 4.15,-2.56 a 0.75,0.75 0 0 0 -0.74,0.68 0.75,0.75 0 0 0 0.68,0.82 l 2.88,0.27 A 0.75,0.75 0 0 0 23.79,4.77 0.75,0.75 0 0 0 23.11,3.95 L 20.23,3.68 a 0.75,0.75 0 0 0 -0.08,-0.00 z M 16.20,3.40 a 0.75,0.75 0 0 0 -0.74,0.72 0.75,0.75 0 0 0 0.72,0.78 l 1.47,0.06 A 0.75,0.75 0 0 0 18.43,4.24 0.75,0.75 0 0 0 17.71,3.46 l -1.47,-0.06 a 0.75,0.75 0 0 0 -0.04,-6.39e-4 z M 12.61,1.30 c -0.41,0.01 -0.80,0.20 -1.12,0.44 -0.02,0.02 -0.04,0.04 -0.06,0.05 L 9.44,2.60 5.84,3.44 4.75,3.25 H 0 v 5.46 h 4.43 l 2.42,3.30 1.49,1.97 c 0.05,0.09 0.11,0.18 0.17,0.27 0.01,0.03 0.03,0.07 0.04,0.08 0.11,0.14 0.15,0.15 0.24,0.23 l 0.03,0.04 0.01,-0.01 c 0.06,0.05 0.18,0.18 0.18,0.18 l 0.03,-0.04 c 0.11,0.06 0.22,0.11 0.35,0.15 0.57,0.15 1.12,-0.07 1.54,-0.39 0.43,-0.32 0.80,-0.76 1.16,-1.31 0.72,-1.09 1.37,-2.61 1.84,-4.34 C 14.41,7.12 14.60,5.48 14.52,4.18 14.48,3.52 14.38,2.95 14.17,2.46 13.96,1.97 13.59,1.50 13.03,1.35 12.88,1.31 12.74,1.30 12.61,1.30 Z m 0.04,1.47 c -0.01,-0.00 0.07,0.01 0.17,0.27 0.11,0.25 0.20,0.68 0.24,1.23 0.07,1.08 -0.10,2.60 -0.53,4.21 -0.43,1.61 -1.05,3.01 -1.64,3.91 -0.30,0.45 -0.60,0.78 -0.82,0.94 -0.22,0.16 -0.29,0.14 -0.28,0.14 0.01,0.00 -0.07,-0.02 -0.17,-0.27 C 9.50,12.96 9.40,12.53 9.37,11.99 9.30,10.90 9.47,9.39 9.90,7.77 10.33,6.16 10.95,4.77 11.55,3.86 11.84,3.41 12.14,3.08 12.36,2.92 12.58,2.75 12.65,2.78 12.65,2.77 Z"
    />
  </SvgIcon>
);

export const SystemRoofIcon = (props) => (
  <SvgIcon {...props} data-testid="SystemRoofIcon">
    <path
      fillRule="evenodd"
      d="m 15.86,1.39 c -0.12,2.03e-5 -0.24,0.04 -0.34,0.10 L 0.69,11.60 c -0.30,0.21 -0.37,0.61 -0.17,0.91 0.21,0.30 0.61,0.37 0.91,0.17 L 6.62,9.11 13.15,18.30 c 0.19,0.27 0.56,0.33 0.83,0.15 l 9.15,-6.11 c 0.28,-0.19 0.36,-0.57 0.16,-0.85 L 16.36,1.65 C 16.24,1.48 16.06,1.39 15.86,1.39 Z M 6.52,10.87 2.40,13.77 v 8.34 H 21.31 l -0.06,-7.10 -7.50,5.00 c -0.27,0.18 -0.64,0.11 -0.83,-0.15 z"
    />
  </SvgIcon>
);

export const SystemWallIcon = (props) => (
  <SvgIcon {...props} data-testid="SystemWallIcon">
    <path
      fillRule="evenodd"
      d="M 18.10,9.13 V 5.63 l -3.98,-2.30 -9.36,5.40 3.98,2.30 -3.04e-5,3.50 z M 13.47,12.89 7.79,16.17 3.82,13.87 0.14,16 4.11,18.29 v 3.50 l 9.36,-5.40 V 12.89 Z M 19.79,4.65 19.04,5.08 V 9.67 l -4.63,2.67 v 3.50 l 9.36,-5.40 V 6.94 Z"
    />
  </SvgIcon>
);

export const SystemFnshIcon = (props) => (
  <SvgIcon {...props} data-testid="SystemFnshIcon">
    <path
      fillRule="evenodd"
      d="m 19.58,7.88 -1.50,0.63 1.08,2.59 1.50,-0.63 z m 1.04,-2.44 -0.38,1.58 2.73,0.66 0.38,-1.58 z m 0.71,-3.98 -1.90,1.74 1.10,1.20 1.90,-1.74 z m -3.29,-1.05 -1.62,0.18 0.28,2.50 1.62,-0.18 z M 13.12,2.90 0.65,9.89 4.89,12.31 16.95,5.44 17.21,5.26 Z m 3.28,4.73 -4.65,2.65 v 7.81 l -1.02,0.63 V 10.86 l -5.43,3.09 -0.81,-0.00 -2.88,-1.64 0.01,8.86 4.12,2.42 10.65,-6.41 z m -3.28,4.01 v 1.28 l -0.93,0.57 v -1.28 z m -2.84,1.64 v 1.28 L 9.36,15.13 V 13.85 Z"
    />
  </SvgIcon>
);

export const SystemApplIcon = (props) => (
  <SvgIcon {...props} data-testid="SystemApplIcon">
    <path
      fillRule="evenodd"
      d="m 18,10.80 h 3.60 V 13.2 H 18 Z m 0,-4.80 h 3.60 V 8.40 H 18 Z m -15.60,0 H 15.6 V 16.8 H 2.40 Z M 0.57,3.60 C 0.26,3.60 -2.63e-5,3.86 -6.01e-8,4.18 V 19.82 C -2.81e-5,20.14 0.26,20.40 0.57,20.40 H 23.43 C 23.74,20.40 24.00,20.14 24,19.82 V 4.18 c 2.8e-5,-0.32 -0.26,-0.58 -0.57,-0.58 z"
    />
  </SvgIcon>
);

export const SystemLandIcon = (props) => (
  <SvgIcon {...props} data-testid="SystemLandIcon">
    <path
      fillRule="evenodd"
      d="m 9.185006,11.251846 9.895863,1.976263 0.959049,-1.058858 c -0.419845,-1.64715 -2.030531,-1.93888 -2.895538,-2.1771911 0.327592,-1.2225929 -0.829932,-2.066335 -1.767767,-2.3643882 -0.910886,-0.2894885 -2.545976,0.205645 -2.727709,0.8838834 -1.924714,0 -3.0102015,1.1294662 -3.2959669,2.1959569 z M 3.565164,0.00825733 1.2281063,13.279412 3.787444,16.272769 3.9024293,17.961831 c 0.024233,0.687582 0.7460934,1.303573 1.3737017,1.665923 l 4.6266209,1.538057 c 2.8086911,0.752586 4.6299691,1.438767 7.5315281,-1.84171 L 23.053236,12.97136 21.539562,12.565658 19.739856,14.375657 7.8779684,12.101077 10.542134,-9.6782114e-6 H 5.5195734 L 4.2406309,11.367638 2.2996927,12.253213 4.5171727,0.01163558 Z M 4.1453588,12.384037 3.8126691,14.792902 2.3462337,13.136541 Z"
    />
  </SvgIcon>
);

export const BookmarkPlusIcon = (props) => (
  <SvgIcon {...props} data-testid="BookmarkPlusIcon">
    <path
      fillRule="evenodd"
      d="m 21.83,3 h -10 c -1.1,0 -1.99,0.9 -1.99,2 l -0.01,16 7,-3 7,3 V 5 c 0,-1.1 -0.9,-2 -2,-2 z m 0,15 -5,-2.18 -5,2.18 V 5 h 10 z M 8.13,10 v 2 H 5.13 v 3 H 3.13 V 12 H 0.13 V 10 H 3.13 V 7 h 2 v 3 z"
    />
  </SvgIcon>
);

export const PerformIcon = (props) => (
  <SvgIcon {...props} data-testid="PerformIcon">
    <path fillRule="evenodd" d="M 9.52,5.52 10.22,2.87 h 7.44 l -9.7e-5,2.65 z m -2.04,7.74 0.69,-2.62 9.49,-7e-6 -2e-6,2.62 z M 17.66,21.09 H 5.41 v 0 l 0.70,-2.65 11.55,2e-6 z" />
  </SvgIcon>
);

export const CondenseIcon = (props) => (
  <SvgIcon {...props} data-testid="CondenseIcon">
    <path fillRule="evenodd" d="M 12,14 8,18 9.41,19.41 11,17.83 V 22 h 2 V 17.83 L 14.59,19.41 16,18 Z M 13,2 H 11 V 6.17 L 9.41,4.59 8,6 12,10 16,6 14.59,4.59 13,6.17 Z m -9,9 h 16 v 2 H 4 Z" />
  </SvgIcon>
);

export const EditNoteIcon = props => (
  <SvgIcon {...props} data-testid="EditNoteIcon">
    <path fillRule="evenodd" d="M3,10h11v2H3V10z M3,8h11V6H3V8z M3,16h7v-2H3V16z M18.01,12.87l0.71-0.71c0.39-0.39,1.02-0.39,1.41,0l0.71,0.71 c0.39,0.39,0.39,1.02,0,1.41l-0.71,0.71L18.01,12.87z M17.3,13.58l-5.3,5.3V21h2.12l5.3-5.3L17.3,13.58z" />
  </SvgIcon>
)

export const CameraIndoorIcon = props => (
  <SvgIcon {...props} data-testid="cameraIndoorIcon">
    <path fillRule="evenodd" d="M 12,0.75 3,7.5 V 21 H 21 V 7.5 Z M 16.5,15.4425 14.25,14.25 v 1.125 c 0,0.61875 -0.50625,1.125 -1.125,1.125 h -4.5 C 8.00625,16.5 7.5,15.99375 7.5,15.375 v -4.5 C 7.5,10.25625 8.00625,9.75 8.625,9.75 h 4.5 c 0.61875,0 1.125,0.50625 1.125,1.125 V 12 l 2.25,-1.1925 z" />
  </SvgIcon>
)

export const RealEstateAgentIcon = props => (
  <SvgIcon {...props} data-testid="realEstateAgentIcon">
    <path fillRule="evenodd" d="M1 22h4V11H1zm19-5h-7l-2.09-.73.33-.94L13 16h2.82c.65 0 1.18-.53 1.18-1.18 0-.49-.31-.93-.77-1.11L8.97 11H7v9.02L14 22l8-3c-.01-1.1-.89-2-2-2M14 1.5l-7 5V9h2l8.14 3.26C18.26 12.71 19 13.79 19 15h2V6.5zm-.5 8.5h-1V9h1zm0-2h-1V7h1zm2 2h-1V9h1zm0-2h-1V7h1z" />
  </SvgIcon>
)